import * as Yup from 'yup';
import { useCallback, useState, useMemo, useEffect, useContext } from 'react';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import { useFormikWrapper } from '@/shared/hooks/formikWrapper';
import { ERROR_MESSAGES } from '@/shared/constants/error-messages';
import * as yup from 'yup';

import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { CustomersController } from '@/shared/endpoints/customers';
import { IMedication } from '@/shared/types/interfaces/medication';
import { Button, RadioGroup } from '@/shared/ui';
import { ITransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { copayEligiblePotencyCodes } from '@/shared/constants/potency-code';
import { makeRequest } from '@/shared/services/http';
import { isNumberZeroOrGreater } from '@/shared/helpers/validations';
import { UnitMedicationForm } from '../unit-medication-form';
import { selectSelectedMedication } from '@/store/slices/onboarding/selectors';
import { ABSOLUTE_ROUTES } from '@/shared/constants/routes';
import { MedicationFlowBox } from '../add-medication';
import { PackagingEnum } from '@/shared/types/enums/packaging';
import { MedicationContext } from '../../../outletWrapper';
import { AddMedicationProvider } from '@/modules/onboarding/pages/medbox/add-flow-wrapper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { OtcPillTip, NonPillQuantityTip, OtcPillTipSeparate } from '../medbox-layout';
import { SeparatePackagingQuantityInput } from './components/SeparatePackagingQuantityInput';
import { IncludedInMedBoxInputs } from './components/IncludedInMedBoxInputs';


export const packagingOptions = [
    { label: 'Include in Medication roll', value: 'Include in my MedBox' },
    { label: 'Package separately', value: 'Package separately' },
];


export const initialValues = {
    packaging: 'Include in my MedBox',
    quantity: null,
    tabletsPerDose: null
};

export const otcMedicationValidationSchema = yup.object().shape({
    packaging: yup.string().required(ERROR_MESSAGES.REQUIRED_FIELD_MISSING),
    quantity: yup
        .number()
        .when('packaging', {
            is: 'Include in my MedBox',
            then: (schema) => schema.required('Please enter the times per day to proceed').max(32, 'Quantity cannot exceed 32 per day'),
            otherwise: (schema) => schema.required('Please enter the total count').max(960, 'Quantity cannot exceed 960')
        }),
    tabletsPerDose: yup
        .number()
        .nullable()
        .when('packaging', {
            is: 'Include in my MedBox',
            then: (schema) => schema.required('Please enter the tablets per dose').max(32, 'Quantity cannot exceed 32 per dose'),
            otherwise: (schema) => schema.nullable().optional(),
        }),
});

export type OtcMedicationFormValues = {
    packaging: string;
    quantity: string | null;
    tabletsPerDose: string | null;
};

const OtcMedicationQuantitySplitter = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
    const selectedMedication = useAppSelector(selectSelectedMedication);
    const medication = selectedMedication?.prescribable;
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { handleAddMedication } = useContext(MedicationContext)!;
    const { setPackaging, packaging } = useContext(AddMedicationProvider)!;

    useEffect(() => {
        if (!medication) {
            navigate(ABSOLUTE_ROUTES.ONBOARDING.PRESCRIPTIONS.ADD_MEDICATION_FLOW.ADD_MEDICATION);
        }
    }, [medication, navigate]);
    const medicationIsPillOrCapsule = medication && copayEligiblePotencyCodes.has(medication.potencyCode || '');

    const finalizeMedication = useCallback(
        async (quantity: string, packaging: PackagingEnum) => {
            // here we can create the medication all the way because we know that there is no additional steps.... 
            try {
                setLoading(true);
                const data = {
                    packaging,
                    quantity,
                    otc: true,
                    prescribable: medication,
                    toPharmacyNpi: import.meta.env.REACT_APP_PHARMACY_NPI || ''
                } as ITransferPrescriptions;
                const result = await dispatch(CustomersController.addTransferPrescription(data));
                if (result.meta.requestStatus === 'fulfilled') {
                    await handleAddMedication(result.payload as ITransferPrescriptions);
                } else {
                    throw new Error('Failed to add medication');
                }
            } catch (error) {
                toast.error('Failed to add medication');
            } finally {
                setLoading(false);
            }
        },
        [dispatch, handleAddMedication, medication]
    );

    const handleGoBack = useCallback(() => {
        navigate(ABSOLUTE_ROUTES.ONBOARDING.PRESCRIPTIONS.ADD_MEDICATION_FLOW.OTC_AS_RX);
    }, [navigate]);

    const relevantComponent = useMemo(() => {
        if (!medication) return null;
        if (medicationIsPillOrCapsule) {
            return <OtcMedicationForm loading={loading} onCreateMedication={finalizeMedication} medication={medication!} isMobile={isMobile} handleGoBack={handleGoBack} onPackagingChange={setPackaging} />;
        } else {
            return <UnitMedicationQuantity loading={loading} onCreateMedication={finalizeMedication} otc medication={medication!} isMobile={isMobile} handleGoBack={handleGoBack} />;
        }
    }, [medication, medicationIsPillOrCapsule, loading, finalizeMedication, isMobile, handleGoBack, setPackaging]);


    const PillTip = useMemo(() => {
        if (!medication) return null;
        if (medicationIsPillOrCapsule) {
            if (packaging !== 'Include in my MedBox') {
                return <OtcPillTipSeparate hasImage={false} customClasses='rounded-bl-none' />;
            }
            return <OtcPillTip hasImage={false} customClasses='rounded-bl-none' />;
        } else {
            return <NonPillQuantityTip hasImage={false} customClasses='rounded-bl-none' />;
        }
    }, [medication, medicationIsPillOrCapsule, packaging]);


    return (
        <div className="flex flex-col w-full">
            <MedicationFlowBox>
                <p className="ph-no-capture font-agrandir text-22 sm:text-28 leading-[140%] sm:leading-[130%] mb-[25px] sm:mb-[15px]">{selectedMedication?.prescribable.name}</p>
                {relevantComponent}
            </MedicationFlowBox>
            {isMobile && (
                <Button
                    fontClassName="font-dmSansMedium font-bold text-17 leading-[130%]"
                    testId="button-medications-select-back"
                    variant="text"
                    className="prescriptions__add-medication__back mt-[20px] w-full"
                    onClick={handleGoBack}
                    disabled={loading}
                >
                    {`< Back`}
                </Button>
            )}
            <div className='flex flex-row justify-center block xl:hidden mt-[20px]'>
                {PillTip}
            </div>
        </div>
    );
}

export const UnitMedicationQuantity = ({ medication, onCreateMedication, handleGoBack, isMobile, loading, ...rest }:
    {
        medication: IMedication,
        onCreateMedication: (quantity: string, packaging: PackagingEnum) => void,
        loading: boolean,
        otc?: boolean,
        handleGoBack?: () => void,
        isMobile?: boolean
    }) => {
    const navigate = useNavigate();
    const formik = useFormikWrapper<{
        quantity: number | string | null;
    }>({
        initialValues: {
            quantity: null
        },
        validateOnChange: false,
        validationSchema: Yup.object().shape({
            quantity: Yup.number().min(1, 'Quantity must be at least 1').required(ERROR_MESSAGES.PLEASE_ENTER_QUANTITY_TO_PROCEED)
        }),
        onSubmit: async values => {
            const quantity = values?.quantity?.toString();
            const packaging = PackagingEnum.Separately;
            onCreateMedication?.(quantity!, packaging);
        }
    });

    const handleGoBackLocal = () => {
        if (handleGoBack) {
            handleGoBack();
            return;
        }
        navigate(ABSOLUTE_ROUTES.ONBOARDING.PRESCRIPTIONS.ADD_MEDICATION_FLOW.OTC_AS_RX);
    };
    return <UnitMedicationForm loading={loading} goBack={handleGoBackLocal} medication={medication!} formik={formik} {...rest} isMobile={isMobile} />;
}

interface IProps {
    medication: IMedication;
    onCreateMedication: (quantity: string, packaging: PackagingEnum) => void;
    loading?: boolean;
}

export const OtcMedicationForm = ({ medication, onCreateMedication, loading, handleGoBack, isMobile, onPackagingChange }: IProps & { handleGoBack?: () => void, isMobile?: boolean, onPackagingChange?: (packaging: string) => void }) => {
    const [otcPriceLoading, setOtcPriceLoading] = useState<{ loading: boolean; value: number | null }>({
        loading: false,
        value: null
    });
    const navigate = useNavigate();
    const formik = useFormikWrapper<OtcMedicationFormValues>({
        initialValues,
        validationSchema: useMemo(() => {
            return otcMedicationValidationSchema;
        }, []),
        onSubmit: (values) => {
            onAddMedication(values);
        }
    })

    const onAddMedication = useCallback(
        (values: OtcMedicationFormValues) => {
            const { quantity, tabletsPerDose, packaging } = values;
            if (!quantity) return;
            const isSeparate = packaging === 'Package separately';
            let calculatedQuantity: string;
            if (isSeparate) {
                calculatedQuantity = parseInt(quantity, 10).toString();
            } else {
                if (!tabletsPerDose) return;
                const parsedQuantity = parseInt(quantity, 10);
                const parsedTabletsPerDose = parseInt(tabletsPerDose, 10);
                calculatedQuantity = (parsedQuantity * parsedTabletsPerDose * 30).toString();
            }
            onCreateMedication(calculatedQuantity, packaging as PackagingEnum);
        },
        [onCreateMedication]
    );

    const fetchOtcPrice = async (quantity: string, medId: string, signal: AbortSignal) => {
        const body = new URLSearchParams();
        body.append('quantity', quantity); // e.g., '90'

        const price = await makeRequest<number>('post', 'prescribables', `${medId}/estimated-price`, body.toString(), {
            signal,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });
        return price;
    };

    const fetchOtcCost = useCallback(async (signal: AbortSignal) => {
        const isSeparate = formik.values.packaging === 'Package separately';
        if (!formik.values.quantity) return;
        if (!isSeparate && !formik.values.tabletsPerDose) return;
        setOtcPriceLoading({ loading: true, value: null });
        const parsedQuantity = parseInt(formik.values.quantity, 10);
        const computedQuantity = isSeparate
            ? parsedQuantity
            : parsedQuantity * parseInt(formik.values.tabletsPerDose!, 10) * 30;
        try {
            const res = await fetchOtcPrice(computedQuantity.toString(), medication.medid.toString(), signal);
            setOtcPriceLoading({ loading: false, value: res });
        } catch (e) {
            setOtcPriceLoading({ loading: false, value: null });
            console.error('Failed to fetch OTC cost', e);
        }
    }, [formik.values.quantity, formik.values.tabletsPerDose, formik.values.packaging, medication.medid]);

    const handleGoBackLocal = () => {
        if (handleGoBack) {
            handleGoBack();
            return;
        }
        navigate(ABSOLUTE_ROUTES.ONBOARDING.PRESCRIPTIONS.ADD_MEDICATION_FLOW.OTC_AS_RX);
    };

    useEffect(() => {
        const isSeparate = formik.values.packaging === 'Package separately';
        if (!formik.values.quantity) return;
        if (!isSeparate && !formik.values.tabletsPerDose) return;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
            fetchOtcCost(controller.signal);
        }, 1000);

        return () => {
            setOtcPriceLoading({ loading: false, value: null });
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, [fetchOtcCost, formik.values.quantity, formik.values.tabletsPerDose, formik.values.packaging]);

    useEffect(() => {
        onPackagingChange?.(formik.values.packaging);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const otcPriceDescription = useMemo(() => {
        if (formik.values.packaging === 'Package separately') {
            return 'This cost reflects the quantity entered. You can edit the quantity above if needed.';
        } else {
            return 'This cost reflects a 30-day supply for the quantity entered. You can edit the amount above if needed.';
        }
    }, [formik.values.packaging]);

    return (
        <div className="otc-medication-form" data-testid="otc-medication-form">
            <div className='flex flex-col gap-[10px]'>
                <div className={`bg-mint-200 flex flex-col rounded-[0.8125rem] p-[1.25rem] sm:p-[1.875rem] ${formik.errors.packaging ? 'border-red-500 border-2' : ''}`}>
                    <p data-testid="otc-medication-form-content-title" className="font-bold atkinsonHyperlegible text-16 md:text-21 mb-[0.9375rem] sm:mb-[1.25rem]">
                        Select Packaging
                    </p>
                    <RadioGroup
                        controlled
                        optionType="radio"
                        items={packagingOptions}
                        name="packaging"
                        dataTestId="medication-quantity-options"
                        radioGroupClassName="w-full gap-[0.625rem]"
                        buttonClassName="select-quantity-radio-button"
                        value={formik.values.packaging}
                        onOptionClick={(e) => {
                            formik.setValues({ ...formik.values, packaging: String(e.value), quantity: null, tabletsPerDose: null });
                            formik.setErrors({});
                            formik.setTouched({});
                            onPackagingChange?.(String(e.value));
                        }}
                    />
                </div>
                {formik.values.packaging === 'Package separately' ? (
                    <SeparatePackagingQuantityInput
                        values={formik.values}
                        errors={formik.errors}
                        touched={formik.touched}
                        setFieldValue={formik.setFieldValue}
                        setErrors={formik.setErrors}
                        handleBlur={formik.handleBlur}
                    />
                ) : (
                    <IncludedInMedBoxInputs
                        values={formik.values}
                        errors={formik.errors}
                        touched={formik.touched}
                        setFieldValue={formik.setFieldValue}
                        setErrors={formik.setErrors}
                        handleBlur={formik.handleBlur}
                    />
                )}
                {isNumberZeroOrGreater(otcPriceLoading.value === null ? undefined : otcPriceLoading.value) && <div className={`relative bg-mint-200 flex flex-col rounded-[0.8125rem] p-5 md:p-7.5`}>
                    <div className='flex flex-col min-h-[48px]'>
                        <div className='flex flex-col gap-[0.625rem]'>
                            <div className='flex flex-row justify-between'>
                                <p className="font-bold font-atkinsonBold text-20">Estimated price</p>
                                <p className="font-bold font-atkinsonBold text-20">
                                    {`${isNumberZeroOrGreater(otcPriceLoading.value === null ? undefined : otcPriceLoading.value) && formik.values.quantity && (formik.values.packaging === 'Package separately' || formik.values.tabletsPerDose) ? `$${otcPriceLoading.value?.toFixed(2)}` : '_'}`}
                                </p>
                            </div>
                            <div className='text-16 font-dmSans text-primary-900'>
                                {otcPriceDescription}
                            </div>
                        </div>
                    </div>
                </div>}
            </div>
            <div className='flex flex-col mt-[25px] sm:mt-[20px] gap-[10px] sm:gap-[15px]'>
                <Button
                    testId="save-btn"
                    variant="contained"
                    type="submit"
                    className="prescriptions__otc_form_save_btn w-full"
                    showEndIcon
                    fontClassName='font-bold'
                    disabled={loading}
                    loading={loading}
                    onClick={() => formik.handleSubmit()}
                >
                    Save & Add
                </Button>
                {!isMobile && <Button
                    variant="outlined"
                    className="prescriptions__add-medication__back w-full"
                    onClick={handleGoBackLocal}
                    testId="button-medication-add-back"
                    fontClassName='font-bold'
                    disabled={loading}
                >
                    Back
                </Button>}
            </div>
        </div>
    );
};

export default withScrollRestoration(OtcMedicationQuantitySplitter);
